import React, { useState, useEffect } from 'react';

const JournalList = ({ journals, fiscalYear, onCameraClick, onJournalClick }) => {
  const [selectedMonth, setSelectedMonth] = useState('all');

  // Group journals by date
  const groupedJournals = React.useMemo(() => {
    let filtered = journals;

    if (selectedMonth !== 'all') {
      const month = parseInt(selectedMonth);
      filtered = journals.filter(j => {
        const date = new Date(j.date);
        return date.getMonth() + 1 === month;
      });
    }

    const groups = {};
    filtered.forEach(journal => {
      const date = new Date(journal.date);
      const dateKey = `${date.getMonth() + 1}月${date.getDate()}日`;
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(journal);
    });

    return Object.entries(groups).sort((a, b) => {
      const [monthA, dayA] = a[0].match(/\d+/g).map(Number);
      const [monthB, dayB] = b[0].match(/\d+/g).map(Number);
      return monthB * 100 + dayB - (monthA * 100 + dayA);
    });
  }, [journals, selectedMonth]);

  const months = [
    { value: 'all', label: '全' },
    ...Array.from({ length: 12 }, (_, i) => ({
      value: String(i + 1),
      label: `${i + 1}月`
    }))
  ];

  const formatAmount = (amount) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num) || num === null || num === undefined) return '0';
    return new Intl.NumberFormat('ja-JP').format(num);
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      display: 'flex',
      flexDirection: 'column',
      background: '#fff',
      paddingTop: '36px'
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px 8px',
        borderBottom: '1px solid #ccc',
        flexShrink: 0
      }}>
        <h2 style={{
          fontSize: '16px',
          fontWeight: 700,
          color: '#333',
          margin: 0
        }}>仕訳一覧</h2>
        <div style={{
          fontSize: '10px',
          color: '#999',
          marginTop: '2px'
        }}>{fiscalYear}年度</div>
      </div>

      {/* Month pills */}
      <div style={{
        display: 'flex',
        gap: '4px',
        padding: '8px 16px',
        overflowX: 'auto',
        borderBottom: '1px solid #eee',
        flexShrink: 0,
        scrollbarWidth: 'none',
        WebkitOverflowScrolling: 'touch'
      }}>
        {months.map(month => (
          <button
            key={month.value}
            onClick={() => setSelectedMonth(month.value)}
            style={{
              flexShrink: 0,
              padding: '4px 12px',
              border: selectedMonth === month.value ? '1px solid #333' : '1px solid #ccc',
              borderRadius: '12px',
              fontSize: '13px',
              fontWeight: selectedMonth === month.value ? '600' : '400',
              color: selectedMonth === month.value ? '#fff' : '#666',
              background: selectedMonth === month.value ? '#333' : 'transparent',
              cursor: 'pointer'
            }}
          >{month.label}</button>
        ))}
      </div>

      {/* Journal list */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '8px 16px 80px',
        WebkitOverflowScrolling: 'touch'
      }}>
        {groupedJournals.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: '#999',
            fontSize: '14px'
          }}>
            {selectedMonth === 'all' ? '仕訳がありません' : `${selectedMonth}月の仕訳がありません`}
          </div>
        ) : (
          groupedJournals.map(([dateLabel, items]) => (
            <div key={dateLabel}>
              <div style={{
                fontSize: '9px',
                color: '#999',
                marginTop: '10px',
                marginBottom: '4px',
                fontWeight: 600
              }}>{dateLabel}</div>

              {items.map((journal, idx) => (
                <div
                  key={idx}
                  onClick={() => onJournalClick && onJournalClick(journal)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    marginBottom: '6px',
                    cursor: 'pointer',
                    background: '#fff'
                  }}
                >
                  <div style={{
                    flex: 1,
                    minWidth: 0
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: '6px'
                    }}>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#333',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>{journal.description || '（摘要なし）'}</div>
                      {journal.partner && (
                        <div style={{
                          fontSize: '9px',
                          color: '#aaa',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          flexShrink: 0
                        }}>{journal.partner}</div>
                      )}
                    </div>
                    <div style={{
                      fontSize: '10px',
                      color: '#999',
                      marginTop: '2px'
                    }}>
                      {journal.debit_account || '（未設定）'} / {journal.credit_account || '（未設定）'}
                    </div>
                  </div>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: 700,
                    fontFamily: 'monospace',
                    flexShrink: 0,
                    color: '#333'
                  }}>
                    <span style={{
                      fontSize: '11px',
                      fontWeight: 400,
                      color: '#999'
                    }}>¥</span>
                    {formatAmount(journal.amount)}
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      {/* FAB */}
      <button
        onClick={onCameraClick}
        style={{
          position: 'absolute',
          bottom: '20px',
          right: '16px',
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          border: '2px solid #333',
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '20px',
          zIndex: 5,
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}
      >📷</button>
    </div>
  );
};

export default JournalList;
